export type PaymentMethodType = "card" | "ach" | "check";

export interface IPaymentHistoryItem {
  id: string;
  documentLabel: "Invoice" | "Sales Receipt";
  documentNumber: string;
  paymentDate: string | null;
  paymentAmount: number;
  paymentStatus: string;
  paymentMethod: string;
  vendorName: string;
}

// Response shape for loading the public payment page before Stripe Elements is initialized.
export interface IPaymentLinkContext {
  token: string;
  expiresAt: string;
  publishableKey: string;
  order: {
    id: string;
    deliveryAt: string;
    paymentStatus: string;
    stripeInvoiceId: string;
    quickbooksInvoiceId?: string;
    service?: string;
    address: string;
    city: string;
    zipCode: string;
    customerName: string;
    baseAmount: number;
    paidAt?: string | null;
  };
}

// Response shape returned when creating/reusing a PaymentIntent for the selected payment method.
export interface IPaymentIntentResponse {
  paymentId: string;
  paymentIntentId: string;
  clientSecret: string;
  publishableKey: string;
  paymentStatus: string;
  baseAmount: number;
  feeAmount: number;
  totalAmount: number;
  feeMode: string;
  feeFormulaVersion: string;
}

export interface IPaymentHistoryResponse {
  payments: IPaymentHistoryItem[];
}

// Minimal fetch helper for public payment endpoints that do not use auth interceptors.
const getJson = async <T,>(url: string, init?: RequestInit): Promise<T> => {
  const response = await fetch(url, {
    headers: {
      "Content-Type": "application/json",
    },
    ...init,
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error((data && data.message) || "Payment request failed");
  }
  return data as T;
};

export const PaymentModel = {
  // Reads token-bound payment context used to render summary + status.
  getContext: (token: string) =>
    getJson<IPaymentLinkContext>(`/api/payments/link/${token}`),
  // Creates or reuses PaymentIntent and returns client secret for Stripe.js confirmation.
  createIntent: (token: string, paymentMethodType: PaymentMethodType) =>
    getJson<IPaymentIntentResponse>(`/api/payments/link/${token}/intent`, {
      method: "POST",
      body: JSON.stringify({ paymentMethodType }),
    }),
  // Payment history lives inside the authenticated profile area, so this call uses the normal
  // bearer-token session flow instead of the public token-based payment helpers above.
  getPaymentHistory: () =>
    getJson<IPaymentHistoryResponse>("/api/payments/history", {
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${localStorage.getItem("token")}`,
      },
    }),
};
